import { Link, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { filterApi } from "@/queries/search";
import { toast } from "sonner";
import { convertDateTime } from "@/lib/utils";
import DatePickerInput from "@/components/ui/date-picker";
import NumberWithHoverPoints from "@/components/NumberWithHoverPoint";
import type { PaginationState, RowSelectionState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { useQueryState } from "nuqs";
import OperatorsIcon from "@/components/OperatorIcons";
import { operators } from "@/constants/operators";
import type { OperatorsName } from "@/stores/operator-store";
import { exportToExcel, prepareDataForExport } from "@/lib/excelExport/excel";
import { Badge } from "@/components/ui/badge";
import {
  ArrowDown,
  Calendar,
  RefreshCw,
  SquarePen,
  TrendingUp,
  Wallet,
} from "lucide-react";
export const Route = createFileRoute("/wallet/extra/")({
  component: WalletTransactionExtra,
});

interface WalletTransactionExtraFilter {
  created_at_from: Date | undefined;
  created_at_to: Date | undefined;
}

function WalletTransactionExtra() {
  const [filtered, setFiltered] = useState<any>(null);
  const [sumValue, setSum] = useState("");
  const [sumTakenValue, setTakenValue] = useState("");
  const [meta, setMeta] = useState<any>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [createdFrom, setCreatedFrom] = useQueryState("f");
  const [createdEnd, setCreatedEnd] = useQueryState("e");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRow, setSelectedRow] = useState<Array<any>>([]);

  useEffect(() => {
    if (filtered) {
      const allData = [...selectedRow, ...filtered];
      console.log(allData);
      const selectedRows = Object.keys(rowSelection)
        .map((key) => {
          return allData.find((item: any) => {
            if (item.id == key) {
              return true;
            }
          });
        })
        .filter(Boolean);

      setSelectedRow(selectedRows);
    }
  }, [rowSelection, filtered]);

  const filterForm = useForm<WalletTransactionExtraFilter>({
    defaultValues: {
      created_at_from: undefined,
      created_at_to: undefined,
    },
  });

  const filterMutation = useMutation({
    mutationFn: ({
      items,
    }: {
      items: {
        created_at: string | null;
        sum?: string;
        third_party_status?: string;
      };
    }) =>
      filterApi(
        "walletTransactionExtra",
        items,
        pagination.pageIndex + 1,
        pagination.pageSize,
      ),
    onSuccess(data, variables) {
      if (!variables.items.sum) {
        setFiltered(data.data.data);
        setMeta(data.data.meta);
      }
    },
    onError() {
      toast.error("خطا در فیلتر تراکنش ها");
    },
  });

  const excelMutationExport = useMutation({
    mutationFn: ({
      items,
    }: {
      items: {
        created_at: string | null;
        sum?: string;
        third_party_status?: string;
      };
    }) => filterApi("walletTransactionExtra", items, 1, meta.total),
    mutationKey: ["excel", "export"],
    onSuccess(data, variables) {
      // Process the data for export
      const table = "walletTransactionExtra";
      const processedData = data.data.data;

      // Prepare data for Excel export
      const exportData = prepareDataForExport(processedData, table);
      const tables: any = {
        walletTransaction: "تراکنش",
        walletTransactionExtra: "مبالغ دریافتی",
      };
      // Export to Excel
      exportToExcel(
        exportData,
        `${tables[table]}_${convertDateTime(new Date())}.xlsx`,
      );
    },
    onError() {
      toast.error("خطا در خروجی اکسل");
    },
  });

  useEffect(() => {
    if (createdFrom) {
      handleFilter();
    }
  }, [pagination]);

  const convertDate = (date: any) => {
    return dayjs(date).calendar("gregory").format("YYYY-MM-DD");
  };

  const handleFilter = () => {
    const created_at_from = createdFrom;
    const created_at_to = createdEnd;
    let created_at = created_at_from;

    if (created_at_to !== "Invalid Date") {
      created_at = `${created_at_from},${created_at_to}`;
    }
    setRowSelection({});
    filterMutation.mutate({ items: { created_at, third_party_status: "1" } });
    filterMutation.mutateAsync(
      { items: { created_at, sum: "value", third_party_status: "1" } },
      {
        onSuccess(data) {
          if (typeof data.data.data.value == "string") {
            console.log(data.data.data.value);
            setSum(data.data.data.value);
          }
          filterMutation.mutateAsync(
            {
              items: {
                created_at,
                sum: "taken_value",
                third_party_status: "1",
              },
            },
            {
              onSuccess(data) {
                if (typeof data.data.data.taken_value == "string") {
                  setTakenValue(data.data.data.taken_value);
                }
              },
            },
          );
        },
      },
    );
  };

  const handleExport = () => {
    const created_at_from = createdFrom;
    const created_at_to = createdEnd;
    let created_at = created_at_from;

    if (created_at_to !== "Invalid Date") {
      created_at = `${created_at_from},${created_at_to}`;
    }

    excelMutationExport.mutate({
      items: { created_at, third_party_status: "1" },
    });
  };

  const handleClearFilter = () => {
    setCreatedFrom(null);
    setCreatedEnd(null);
    setFiltered(null);
    setRowSelection({});
    setSum("");
  };

  const columns = [
    {
      id: "purchase",
      header: "خرید",
      cell: ({ row }: { row: any }) => {
        const transaction = row.original;
        const operator = operators.find(
          (op) => op.id === transaction.operator_id,
        );

        return (
          <div className="space-y-1.5 text-xs leading-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {transaction.mobile && operator ? (
                  <OperatorsIcon
                    operator={operator.name as OperatorsName}
                    className="size-7 shrink-0"
                    operatorColors
                  />
                ) : null}
                <div className="min-w-0">
                  <Badge
                    variant="outline"
                    className={
                      transaction.mobile
                        ? "mb-1 border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
                        : "mb-1 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                    }
                  >
                    {transaction.mobile ? "شارژ مستقیم" : "کارت شارژ"}
                  </Badge>
                  <div dir="ltr" className="text-right tabular-nums">
                    {transaction.mobile
                      ? `0${transaction.mobile.slice(1)}`
                      : "کارت شارژ"}
                  </div>
                  <div className="break-words font-medium">
                    {transaction.name || "-"}
                  </div>
                </div>
              </div>
              <Link to={`/wallet/extra/$id`} params={{ id: transaction.id }}>
                <Button
                  size="icon"
                  variant="outline"
                  className="size-8 shrink-0"
                  aria-label="ویرایش مبلغ دریافتی"
                  title="ویرایش مبلغ دریافتی"
                >
                  <SquarePen className="size-4" />
                </Button>
              </Link>
            </div>
            <Badge
              variant="secondary"
              className="max-w-full whitespace-normal leading-4"
            >
              اپراتور: {transaction.mobile ? operator?.title || "-" : "-"}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "amounts",
      header: "مبالغ",
      cell: ({ row }: { row: any }) => {
        const transaction = row.original;

        return (
          <div className="space-y-2 text-xs leading-5">
            <div className="rounded-md border border-amber-200 bg-amber-50/80 p-2 dark:border-amber-900 dark:bg-amber-950/30">
              <Badge className="mb-1 border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                <ArrowDown />
                برداشت
              </Badge>
              <div className="font-semibold text-amber-900 dark:text-amber-100">
                <NumberWithHoverPoints number={transaction.value || ""} />
              </div>
            </div>
            <div className="rounded-md border border-sky-200 bg-sky-50/80 p-2 dark:border-sky-900 dark:bg-sky-950/30">
              <Badge className="mb-1 max-w-full whitespace-normal border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900/60 dark:text-sky-200">
                <Wallet />
                دریافتی از مشتری
              </Badge>
              <div className="font-semibold text-sky-900 dark:text-sky-100">
                <NumberWithHoverPoints number={transaction.taken_value || ""} />
              </div>
            </div>
            <div className="rounded-md border border-emerald-200 bg-emerald-50/80 p-2 dark:border-emerald-900 dark:bg-emerald-950/30">
              <Badge className="mb-1 border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
                <TrendingUp />
                سود شما
              </Badge>
              <div className="font-bold text-emerald-700 dark:text-emerald-300">
                <NumberWithHoverPoints
                  number={Number(transaction.taken_value - transaction.value)}
                />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "dates",
      header: "تاریخ‌ها",
      cell: ({ row }: { row: any }) => {
        return (
          <div className="space-y-2 text-xs leading-5">
            <div className="rounded-md border border-border bg-muted/30 p-2">
              <Badge variant="outline" className="mb-1 bg-background/80">
                <Calendar />
                ایجاد
              </Badge>
              <div dir="ltr" className="text-right tabular-nums">
                {convertDateTime(row.original.created_at)}
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/30 p-2">
              <Badge variant="outline" className="mb-1 bg-background/80">
                <RefreshCw />
                ویرایش
              </Badge>
              <div dir="ltr" className="text-right tabular-nums">
                {convertDateTime(row.original.updated_at)}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardHeader className="p-3 lg:p-6">
            <CardTitle className="text-base lg:text-2xl">
              مبالغ دریافتی از مشتری
            </CardTitle>
          </CardHeader>
          <Link to="/">
            <Button className="m-3 lg:m-4">بازگشت</Button>
          </Link>
        </div>

        <CardContent className="p-2 pt-0 lg:p-6 lg:pt-0">
          <div className="flex flex-col gap-2 mb-4">
            <h1 className="text-xl font-bold">فیلتر ها:</h1>
            <form
              className="grid grid-cols-1 lg:grid-cols-4 gap-2"
              onSubmit={filterForm.handleSubmit(handleFilter)}
            >
              <div className="flex flex-col gap-2 items-stretch">
                <DatePickerInput
                  label="از تاریخ"
                  value={
                    typeof createdFrom == "string"
                      ? new Date(createdFrom)
                      : undefined
                  }
                  onChange={(e) => setCreatedFrom(convertDate(e))}
                />
              </div>
              <div className="flex flex-col gap-2 items-stretch">
                <DatePickerInput
                  label="تا تاریخ"
                  value={
                    typeof createdEnd == "string"
                      ? new Date(createdEnd)
                      : undefined
                  }
                  onChange={(e) => setCreatedEnd(convertDate(e))}
                  minDate={
                    typeof createdFrom == "string"
                      ? new Date(createdFrom)
                      : undefined
                  }
                />
              </div>
              <div className="col-span-1 flex flex-wrap gap-2 self-end">
                <Button
                  type="submit"
                  disabled={!createdFrom}
                  isLoading={filterMutation.isPending}
                >
                  فیلتر
                </Button>
                {filtered && (
                  <Button variant="secondary" onClick={handleClearFilter}>
                    پاک کردن
                  </Button>
                )}
                {filtered && (
                  <Button
                    type="button"
                    isLoading={excelMutationExport.isPending}
                    disabled={excelMutationExport.isPending}
                    className="bg-green-500 hover:bg-green-700"
                    onClick={handleExport}
                  >
                    خروجی اکسل
                  </Button>
                )}
              </div>
            </form>
            {filtered && sumValue !== "" && sumTakenValue !== "" && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-amber-200 bg-amber-50/80 p-2 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                  <Badge className="mb-1 border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                    <ArrowDown />
                    مجموع برداشت
                  </Badge>
                  <div>
                    <span>
                      {Intl.NumberFormat("fa-ir").format(
                        Number(sumValue?.split(".")[0] || sumValue),
                      )}{" "}
                      ریال
                    </span>
                  </div>
                </div>
                <div className="rounded-md border border-sky-200 bg-sky-50/80 p-2 text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100">
                  <Badge className="mb-1 max-w-full whitespace-normal border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900/60 dark:text-sky-200">
                    <Wallet />
                    مجموع دریافتی
                  </Badge>
                  <div>
                    {Intl.NumberFormat("fa-ir").format(
                      Number(sumTakenValue?.split(".")[0] || sumTakenValue),
                    )}{" "}
                    ریال
                  </div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50/80 p-2 font-bold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                  <Badge className="mb-1 border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
                    <TrendingUp />
                    مجموع سود
                  </Badge>
                  <div>
                    {Intl.NumberFormat("fa-ir").format(
                      Number(sumTakenValue?.split(".")[0] || sumTakenValue) -
                        Number(sumValue?.split(".")[0] || sumValue),
                    )}{" "}
                    ریال
                  </div>
                </div>
              </div>
            )}
          </div>
          {selectedRow.length > 0 && (
            <div className="sticky top-24 z-10 flex min-h-20 flex-col gap-3 border-t border-border bg-card p-2 md:flex-row md:items-center md:justify-between">
              <div className="shrink-0">
                <Badge
                  variant="secondary"
                  className="max-w-full whitespace-normal px-3 py-1"
                >
                  تعداد انتخاب‌شده: {selectedRow.length}
                </Badge>
              </div>

              <div className="grid w-full grid-cols-1 gap-2 text-sm sm:grid-cols-3 md:w-auto">
                <div className="rounded-md border border-amber-200 bg-amber-50/80 p-2 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                  <Badge className="mb-1 border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                    برداشت
                  </Badge>
                  <div>
                    <NumberWithHoverPoints
                      number={selectedRow.reduce(
                        (acc, curr) => acc + Number(curr.value),
                        0,
                      )}
                    />
                  </div>
                </div>
                <div className="rounded-md border border-sky-200 bg-sky-50/80 p-2 text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100">
                  <Badge className="mb-1 border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900/60 dark:text-sky-200">
                    دریافتی
                  </Badge>
                  <div>
                    <NumberWithHoverPoints
                      number={selectedRow.reduce(
                        (acc, curr) => acc + Number(curr.taken_value),
                        0,
                      )}
                    />
                  </div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50/80 p-2 font-bold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
                  <Badge className="mb-1 border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200">
                    سود
                  </Badge>
                  <div>
                    <NumberWithHoverPoints
                      number={
                        selectedRow.reduce(
                          (acc, curr) => acc + Number(curr.taken_value),
                          0,
                        ) -
                        selectedRow.reduce(
                          (acc, curr) => acc + Number(curr.value),
                          0,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {filtered && (
            <div className="[&_table]:table-fixed [&_thead]:bg-muted/70 [&_th]:h-10 [&_th]:px-2 [&_td]:p-2 [&_th:first-child]:w-10 [&_td:first-child]:w-10">
              <DataTable<any, any>
                columns={columns}
                data={filtered}
                pagination={pagination}
                onPaginationChange={setPagination}
                totalDataCount={meta?.total || 0}
                enableRowSelection
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                pageCount={meta?.last_page || 0}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
